import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  InputGroup,
  InputRightElement,
  Icon,
  FormErrorMessage,
} from '@chakra-ui/react';
import { useToastNotification } from 'utils/toast-utils';
import { authService } from 'services/auth.service';
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { useFormik } from 'formik';
import { updatePasswordSchema } from 'views/auth/schemas';

const UpdatePasswordModal = ({ isOpen, onClose }) => {
  const { showToast } = useToastNotification();
  const [showPasswords, setShowPasswords] = React.useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: updatePasswordSchema,
    onSubmit: async (values) => {
      try {
        await authService.updatePassword(values);
        
        showToast(
          'Success',
          'Password updated successfully',
          'success'
        );
        onClose();
        formik.resetForm();
      } catch (error) {
        showToast(
          'Error',
          error.response?.data?.message || 'Failed to update password',
          'error'
        );
      }
    }
  });

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader>Update Password</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl 
              isRequired 
              isInvalid={formik.touched.currentPassword && formik.errors.currentPassword}
            >
              <FormLabel>Current Password</FormLabel>
              <InputGroup>
                <Input
                  type={showPasswords.currentPassword ? "text" : "password"}
                  name="currentPassword"
                  value={formik.values.currentPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter current password"
                />
                <InputRightElement>
                  <Icon
                    as={showPasswords.currentPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={() => togglePasswordVisibility('currentPassword')}
                    cursor="pointer"
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{formik.errors.currentPassword}</FormErrorMessage>
            </FormControl>

            <FormControl 
              isRequired
              isInvalid={formik.touched.newPassword && formik.errors.newPassword}
            >
              <FormLabel>New Password</FormLabel>
              <InputGroup>
                <Input
                  type={showPasswords.newPassword ? "text" : "password"}
                  name="newPassword"
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter new password"
                />
                <InputRightElement>
                  <Icon
                    as={showPasswords.newPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={() => togglePasswordVisibility('newPassword')}
                    cursor="pointer"
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{formik.errors.newPassword}</FormErrorMessage>
            </FormControl>

            <FormControl 
              isRequired
              isInvalid={formik.touched.confirmPassword && formik.errors.confirmPassword}
            >
              <FormLabel>Confirm New Password</FormLabel>
              <InputGroup>
                <Input
                  type={showPasswords.confirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Confirm new password"
                />
                <InputRightElement>
                  <Icon
                    as={showPasswords.confirmPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={() => togglePasswordVisibility('confirmPassword')}
                    cursor="pointer"
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{formik.errors.confirmPassword}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button 
            colorScheme="purple" 
            onClick={formik.handleSubmit}
            isLoading={formik.isSubmitting}
            isDisabled={!formik.isValid || formik.isSubmitting}
          >
            Update Password
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UpdatePasswordModal;
